import React, { useCallback, useState, useMemo } from "react";
import { useMedia } from "@/contexts/MediaContext";
import VideoControls from "../VideoControls";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { BuildSteps } from "@/types/flows";
import { useFlow } from "@/contexts/FlowContext";
import { useFilePreview } from "@/contexts/FilePreviewContext";
import { useModel } from "@/contexts/ModelContext";
import { fileCarouselHintContainerId } from "../useCarousel";
import { useFrameStable } from "@/contexts/FrameContext";
import { FileControls } from "./FileControls";
import { FileSwitcher } from "./FileSwitcher";
import { FileUploadModal } from "@/components/file/UploadModal/FileUploadModal";
import { useFlowState } from "@/hooks/useFlowState";
import {
    useFileCarouselNavigation,
    useFileCarouselThumbnails,
    useFileCarouselKeyboardNavigation,
    useFileCarouselAutoScroll
} from "@/hooks/useFileCarousel";
import { FloatingNavigationControls } from "@/components/shared/FloatingNavigationControls";
import { useCurrentMedia } from "@/store";
import { UploadLimitErrorType } from "@/constants/media";
import { FileInputArea } from "@/components/flow/build/Steps/Upload/VisualAreaContent/FileInputArea";

// Container ID constants to avoid hardcoded strings
const THUMBNAIL_CONTAINER_ID = "thumbnail-container";

interface FileCarouselProps {
    children: React.ReactNode;
    className?: string;
    onAddMediaItems: (files: File[], onFinally?: () => void) => Promise<void>;
    uploadLimitError?: UploadLimitErrorType | null;
    clearUploadLimitError?: () => void;
}

export const FileCarousel: React.FC<FileCarouselProps> = ({
    children,
    className,
    onAddMediaItems,
    uploadLimitError,
    clearUploadLimitError
}) => {
    const { flowLocked } = useFlow();
    const { step } = useFlowState();
    const { hasMultipleFiles, videoControls } = useFilePreview();
    const currentMedia = useCurrentMedia();
    const { deleteMediaItem, erroredMediaItems, orderedMediaItems } = useMedia();
    const { isTestingNewFiles, isDownloading } = useModel();

    // Auto-generate predictions on frame/file navigation
    // useAutoPredictions();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const isReviewStep = useMemo(() => step === BuildSteps.REVIEW, [step]);
    const { orderedTimelineFramePreviewUrls } = useFrameStable();

    // Use shared hooks
    const thumbnails = useFileCarouselThumbnails(
        orderedMediaItems,
        orderedTimelineFramePreviewUrls
    );
    const { handlePrevFile, handleNextFile } = useFileCarouselNavigation({
        mediaItems: orderedMediaItems,
        fileId: currentMedia?.file.id,
        flowLocked,
        hasMultipleFiles
    });

    useFileCarouselAutoScroll({
        fileId: currentMedia?.file.id,
        hasMultipleFiles,
        containerId: THUMBNAIL_CONTAINER_ID
    });
    useFileCarouselKeyboardNavigation(hasMultipleFiles, flowLocked, handlePrevFile, handleNextFile);

    const closeModal = useCallback(() => {
        clearUploadLimitError?.();
        setIsModalOpen(false);
    }, [clearUploadLimitError]);

    const addMediaItems = useCallback(
        async (files: File[]) => {
            if (flowLocked) {
                console.warn("🎠 Carousel: Flow is locked, returning");
                return;
            }

            await onAddMediaItems(files, closeModal);
        },
        [onAddMediaItems, closeModal, flowLocked]
    );

    const hasNoFiles = orderedMediaItems.length === 0;

    // Always render the carousel structure, but conditionally show navigation buttons
    return (
        <div className={twClassNames("flex h-full w-full flex-row gap-4", className)}>
            <FileSwitcher
                orderedMediaItems={orderedMediaItems}
                hasMultipleFiles={hasMultipleFiles}
                thumbnails={thumbnails}
                deleteMediaItem={deleteMediaItem}
                isTestingNewFiles={isTestingNewFiles}
                isReviewStep={isReviewStep}
                setIsModalOpen={setIsModalOpen}
                onAddMediaItems={addMediaItems}
                isDownloading={isDownloading}
                containerId={THUMBNAIL_CONTAINER_ID}
            />
            <div
                className={twClassNames(
                    "fileCarouselWindow relative flex flex-1 flex-col",
                    !videoControls && "overflow-hidden rounded-b-xl",
                    flowLocked && "cursor-not-allowed"
                )}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <FileControls filename={currentMedia?.file.file?.name} flowLocked={flowLocked} />

                <div
                    className={twClassNames(
                        "relative flex flex-1 flex-col",
                        flowLocked && "cursor-not-allowed"
                    )}
                >
                    {/* portal for children to render hints */}
                    <div id={fileCarouselHintContainerId} className="floatingHint" />
                    {/* main content */}
                    <div
                        className={twClassNames(
                            "flex flex-1 flex-col",
                            flowLocked && "cursor-not-allowed"
                        )}
                    >
                        {hasNoFiles ? (
                            <div className="flex h-full w-full flex-col items-center gap-4 p-4">
                                <FileInputArea
                                    addMediaItems={addMediaItems}
                                    showAllMethods={false}
                                    isUsePage={true}
                                    uploadLimitError={uploadLimitError}
                                    clearUploadLimitError={clearUploadLimitError}
                                />
                            </div>
                        ) : (
                            children
                        )}
                    </div>
                </div>

                {/* Navigation arrows - only show when multiple files and hovered */}
                {hasMultipleFiles && isHovered && !flowLocked && (
                    <FloatingNavigationControls
                        onPrevious={handlePrevFile}
                        onNext={handleNextFile}
                        buttonType="dark-primary"
                        horizontalControls={false}
                    />
                )}

                {/* Video controls - rendered outside the main content */}
                {currentMedia?.file.isVideo && (
                    <VideoControls controls={videoControls} flowLocked={flowLocked} />
                )}
            </div>

            {isModalOpen && (
                <FileUploadModal
                    title={isReviewStep ? "Test on More Files" : "Add Files"}
                    isModalOpen={isModalOpen}
                    setIsModalOpen={setIsModalOpen}
                    onAddMediaItems={addMediaItems}
                    erroredMediaItems={erroredMediaItems}
                    uploadLimitError={uploadLimitError}
                    clearUploadLimitError={clearUploadLimitError}
                />
            )}
        </div>
    );
};
